import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { addDays, addWeeks, differenceInCalendarWeeks, format, isSameDay, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { zhHK } from 'date-fns/locale/zh-HK';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { CalendarPickerIcon } from '@/components/calendar-picker-icon';
import { ROUTE } from '@/routes/constants';
import { isEN } from '@/utils/i18n';
import { CalendarEvent } from '@/components/ui/full-calendar';
import { formatRange } from '@/lib/format-week-range';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AssignmentModel } from '@/services/generatedAPIs.schemas';
import { useThemeAttr } from '@/hooks/use-theme-attr';
import MobileLandscapeDashboardShell, { useStageFit } from './mobile-landscape-dashboard-shell';

// 手机横屏(844×390)學習概覽页主体:數學課業(横向卡片) + 課業日程表(单周),数据由 OverviewPage 传入,逻辑同桌面

export type OverviewCardData = {
  ordinal: number;
  title: string;
  done: number;
  total: number;
  assignments: AssignmentModel[];
};

interface Props {
  carouselData: OverviewCardData[];
  calendarData: CalendarEvent[];
}

// 事件 pill 配色(与 overview-agenda 同表,按 id 稳定取色)
const PILL_PALETTE: { body: string; cap: string }[] = [
  { body: 'rgba(91,201,232,0.22)', cap: '#5BC9E8' },
  { body: 'rgba(255,213,123,0.30)', cap: '#FFD57B' },
  { body: 'rgba(255,158,123,0.28)', cap: '#FF9E7B' },
  { body: 'rgba(124,229,120,0.30)', cap: '#7FE578' },
  { body: 'rgba(225,80,238,0.16)', cap: '#E150EE' },
  { body: 'rgba(180,180,180,0.40)', cap: '#C4C4C4' },
];
function pillColor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PILL_PALETTE[h % PILL_PALETTE.length];
}

function Ring({ percent, color, size = 40 }: { percent: number; color: string; size?: number }) {
  const stroke = 4.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const offset = c - (clamped / 100) * c;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E6E6E6" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      {/* 字号跟随可用内径(size-stroke*2):36 的环内径 27,「100%」四字符 9px(实测 25)、其余 11px(实测 23.9),都不压描边。
          数值用深色而非环色(accent):ZH-03 验收要求「更深的数值颜色 + 与描边分层」,浅绿 #9CEA4F 对白底仅 1.47:1,深色 15.6:1(同桌面 ProgressRing) */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: clamped >= 100 ? 9 : 11, lineHeight: 1, fontWeight: 700, color: '#1A1C1D', fontVariantNumeric: 'tabular-nums' }}>{clamped}%</div>
    </div>
  );
}

export default function MobileLandscapeOverview({ carouselData, calendarData }: Props) {
  const { t, i18n } = useTranslation();
  const theme = useThemeAttr();
  // 宽屏铺满:取与舞台壳一致的 shift(见 useStageFit)。两个面板吃掉 shift 变宽,面板内右侧控制键(carousel next / 日程 nav)整体右移贴右,日历 7 列把 shift 均分铺满。
  const { shift } = useStageFit();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  // 日期选择器(手机横屏改弹窗)：弹窗开合 + 待确认日(draft) + 确认后高亮的所选日
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Date | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // 主题驱动配色(同桌面 useOverviewAccent)：橙=品牌绿系,其余=var(--primary)/浅底
  const accent = theme === 'orange' ? '#9CEA4F' : 'var(--primary)';
  const panelBg = theme === 'orange' ? '#D0FFCD' : '#FEFEFE';
  const arrow = theme === 'orange' ? '#7DD02A' : 'var(--primary)';
  // 課業輪播箭頭符號：默認主題黑色,其餘主題白色(深底上黑箭頭辨識度不足)
  const arrowFg = theme === 'orange' ? '#1A1C1D' : '#FFFFFF';

  const locale = isEN(i18n) ? enUS : zhHK;
  const monday = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset);
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

  const scrollBy = (dir: number) => scrollerRef.current?.scrollBy({ left: dir * 125, behavior: 'smooth' });

  // 「確定」：把所选日所在的那一周算成 weekOffset(单周视图,故按单周算)跳过去并高亮该日
  const handleConfirm = () => {
    if (draftDate) {
      const weeksDiff = differenceInCalendarWeeks(
        startOfWeek(draftDate, { weekStartsOn: 1 }),
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        { weekStartsOn: 1 },
      );
      setWeekOffset(weeksDiff);
      setSelectedDate(draftDate);
    }
    setPickerOpen(false);
  };

  // 「清除篩選」：回到本周、清掉高亮(复用原「回本周」语义)
  const handleClearFilter = () => {
    setWeekOffset(0);
    setSelectedDate(undefined);
    setDraftDate(undefined);
    setPickerOpen(false);
  };

  const content = (
    <>
      {/* 页面标题 */}
      <h1 style={{ position: 'absolute', left: 204, top: 68, margin: 0, fontSize: 20, fontWeight: 700, color: '#000', fontFamily: 'var(--font-sans)', lineHeight: '24px' }}>
        {t('student.overview')}
      </h1>

      {/* ── 數學課業 面板 ── */}
      <div style={{ position: 'absolute', left: 205, top: 98, width: 627 + shift, height: 135, borderRadius: 20, background: panelBg }}>
        {/* 标题：数学符号 glyph + 數學課業 */}
        <span aria-hidden style={{ position: 'absolute', left: 17, top: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, fontSize: 10, fontWeight: 800, lineHeight: 1, color: '#1A1C1D' }}>
          <span>+</span><span>−</span><span>×</span><span>÷</span>
        </span>
        <span style={{ position: 'absolute', left: 40, top: 12, fontSize: 13, fontWeight: 700, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>
          {t('common.mathsHomework')}
        </span>

        {/* 左右翻页(小圆按钮) — 与卡片滚动区不重叠 */}
        {carouselData.length > 0 && (
          <>
            <button type="button" aria-label={t('common.previous', { defaultValue: 'Previous' })} onClick={() => scrollBy(-1)} style={{ position: 'absolute', left: 8, top: 72, width: 44, height: 44, background: 'transparent', border: 0, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: 36, height: 36, borderRadius: '50%', background: arrow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: arrowFg }} className="hover:brightness-95">
                <ChevronLeft size={20} strokeWidth={2.5} />
              </span>
            </button>
            <button type="button" aria-label={t('common.next', { defaultValue: 'Next' })} onClick={() => scrollBy(1)} style={{ position: 'absolute', left: 571 + shift, top: 72, width: 44, height: 44, background: 'transparent', border: 0, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: 36, height: 36, borderRadius: '50%', background: arrow, display: 'flex', alignItems: 'center', justifyContent: 'center', color: arrowFg }} className="hover:brightness-95">
                <ChevronRight size={20} strokeWidth={2.5} />
              </span>
            </button>
          </>
        )}

        {/* 卡片横向滚动区 — 121 宽(4×121+3×4=496≤499,4 张完整可见不再硬切半张);标题仍两行 clamp(MOB-06)。左右留箭头空间,其余横向滚动进入 */}
        <div ref={scrollerRef} style={{ position: 'absolute', left: 64, top: 34, width: 499 + shift, height: 96, overflowX: 'auto', overflowY: 'hidden', display: 'flex', alignItems: 'center', gap: 4, scrollbarWidth: 'none' }} className="[&::-webkit-scrollbar]:hidden">
          {carouselData.map((data, index) => {
            const percent = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;
            return (
              <NavLink key={index} to={ROUTE.STUDENT.HOMEWORK} state={data.assignments} style={{ flex: '0 0 auto', textDecoration: 'none' }}>
                {/* 竖排三行(同设计图):序号 → 两行标题 → 环。高 92 = 顶 8 / 序号 12 / 间隔 2 / 标题 30 / 环 36 / 底 4 */}
                <div style={{ position: 'relative', width: 121, height: 92, borderRadius: 13, background: '#FFFFFF', boxShadow: '0 6px 18px -12px rgba(0,0,0,0.25)' }} className="transition hover:-translate-y-0.5">
                  <div style={{ position: 'absolute', left: 12, top: 8, fontSize: 12, lineHeight: 1, fontWeight: 500, color: '#94A3B8', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>
                    {t('student.questype')} {String(data.ordinal).padStart(2, '0')}
                  </div>
                  <div
                    title={data.title}
                    style={{
                      position: 'absolute', left: 12, top: 22, right: 12, height: 30, fontSize: 12, lineHeight: 1.25, fontWeight: 700,
                      color: '#1A1C1D', fontFamily: 'var(--font-sans)',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
                    }}
                  >
                    {data.title}
                  </div>
                  <div style={{ position: 'absolute', left: 12, top: 52 }}>
                    <Ring percent={percent} color={accent} size={36} />
                  </div>
                </div>
              </NavLink>
            );
          })}
        </div>
      </div>

      {/* ── 課業日程表 面板(单周) ── */}
      <div style={{ position: 'absolute', left: 205, top: 243, width: 627 + shift, height: 135, borderRadius: 20, background: '#F3F3F4', overflow: 'hidden' }}>
        {/* header 单行 flex：左(图标+標題) / 弹性空隙 / 右(日期區間+三控件)；改前 absolute 手算 left 且垂直中心不齐(控件 30 vs 文字 24)，现统一 cy=24 且窄屏日期先省略号不重叠 */}
        <div style={{ position: 'absolute', left: 17, right: 0, top: 2, height: 44, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarDays style={{ flex: '0 0 auto', width: 15, height: 15, color: '#1A1C1D' }} />
          <span style={{ flex: '0 0 auto', fontSize: 14, fontWeight: 700, color: '#1A1C1D', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}>
            {t('common.calendar')}
          </span>
          <div style={{ flex: '1 1 auto', minWidth: 4 }} />
          {/* 补当周区间：原本全页无月份/年份(跨月周看不出「1、2」是八月)；复用桌面 formatRange，纯 date-fns token 无新增翻译键 */}
          <span style={{ minWidth: 0, marginRight: 2, fontSize: 12, fontWeight: 400, color: '#64748B', fontFamily: 'var(--font-sans)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatRange(days[0], days[6], isEN(i18n), locale)}
          </span>
          {/* MOB-05：三控件各 44×44 热区 + aria-label/title；箭头用 prevWeek/nextWeek(本面板单周，桌面键 prevTwoWeeks 会播报「上兩週」与行为不符) */}
          {([
            { key: 'overview.pickDate', fallback: '選擇日期', icon: <CalendarPickerIcon size={15} />, onClick: () => { setDraftDate(selectedDate ?? new Date()); setPickerOpen(true); } },
            { key: 'overview.prevWeek', fallback: '上一週', icon: <ChevronLeft size={18} strokeWidth={2.5} />, onClick: () => setWeekOffset((o) => o - 1) },
            { key: 'overview.nextWeek', fallback: '下一週', icon: <ChevronRight size={18} strokeWidth={2.5} />, onClick: () => setWeekOffset((o) => o + 1) },
          ] as const).map(({ key, fallback, icon, onClick }) => {
            const label = t(key, { defaultValue: fallback });
            return (
              <button
                key={key}
                type="button"
                aria-label={label}
                title={label}
                onClick={onClick}
                style={{ flex: '0 0 auto', width: 44, height: 44, background: 'transparent', border: 0, cursor: 'pointer', color: '#1A1C1D', lineHeight: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                className="hover:opacity-60"
              >
                {icon}
              </button>
            );
          })}
        </div>

        {/* 7 列：星期 + 日期 + 事件 pill */}
        {days.map((day, i) => {
          // 宽屏铺满:把 shift 均分到 7 列(step 与列宽同增 shift/7),等间距铺满加宽后的面板
          const colW = 75 + shift / 7;
          const left = 23 + i * (86 + shift / 7);
          const isToday = isSameDay(day, new Date());
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const dayEvents = calendarData.filter((ev) => isSameDay(ev.start, day));
          return (
            <div key={day.toISOString()} style={{ position: 'absolute', left, top: 0, width: colW }}>
              <div style={{ position: 'absolute', top: 41, width: colW, textAlign: 'center', fontSize: 12, color: '#94A3B8', fontFamily: 'var(--font-sans)' }}>
                {format(day, 'EEE', { locale })}
              </div>
              {/* 日期数字：今天/所选日为绿字；所选日额外加软底色 chip(确认跳转后高亮) */}
              <div style={{ position: 'absolute', top: 58, width: colW, textAlign: 'center', fontFamily: 'var(--font-sans)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: isSelected ? '0 6px' : 0, borderRadius: 9, fontSize: 12, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: isToday || isSelected ? 'var(--sidebar-active)' : '#94A3B8', backgroundColor: isSelected ? 'color-mix(in srgb, var(--sidebar-active) 18%, transparent)' : 'transparent' }}>
                  {format(day, 'd')}
                </span>
              </div>
              <div style={{ position: 'absolute', top: 78, width: colW, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {dayEvents.map((ev) => {
                  const cp = pillColor(ev.id);
                  const pill = (
                    <div title={ev.title} style={{ position: 'relative', height: 20, borderRadius: 11, background: cp.body, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
                      <span aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 22, borderTopLeftRadius: 11, borderBottomLeftRadius: 11, background: cp.cap }} />
                      <span style={{ position: 'relative', paddingLeft: 26, paddingRight: 6, fontSize: 12, color: '#1A1C1D', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.title}
                      </span>
                    </div>
                  );
                  return ev.data ? (
                    <NavLink key={ev.id} to={ROUTE.STUDENT.HOMEWORK} state={[ev.data]} style={{ textDecoration: 'none' }}>{pill}</NavLink>
                  ) : (
                    <div key={ev.id}>{pill}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return createPortal(
    <>
      <div
        className="hidden [@media(max-height:500px)]:flex fixed inset-0 items-center justify-center overflow-hidden"
        style={{ background: '#FFFFFF' }}
      >
        <MobileLandscapeDashboardShell>{content}</MobileLandscapeDashboardShell>
      </div>

      {/* 日期选择器：手机横屏改「居中弹窗」(替代桌面的锚定 Popover)。DialogContent 自带
          portal 到 document.body,脱离 FitScale 缩放舞台,按真实视口居中(与 practice 弹窗同套路)。 */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          showCloseButton={false}
          showTitle={false}
          className="w-auto gap-2 rounded-[22px] border-0 p-4 shadow-xl"
          style={{ fontFamily: 'var(--font-sans)' }}
        >
          <DialogTitle className="sr-only">{t('common.calendar')}</DialogTitle>
          {/* 颜色沿用桌面默认 Calendar(选中日=bg-primary、今天=bg-accent、翻月箭头=描边、
              确定按钮=--sidebar-active),与电脑端/平板端一致;仅覆盖间距把月历压紧以塞进 390 高。 */}
          <Calendar
            mode="single"
            selected={draftDate}
            onSelect={setDraftDate}
            defaultMonth={draftDate ?? new Date()}
            locale={locale}
            showOutsideDays
            className="p-0"
            classNames={{
              month: 'flex flex-col gap-2',
              row: 'mt-1 flex w-full',
            }}
          />
          {/* 底部按钮区与桌面 overview-agenda 逐字一致：清除篩選=无边框灰字、確定=--sidebar-active 绿 */}
          <div className="mt-1 flex items-center justify-between gap-3 border-t border-black/5 pt-3">
            <button
              type="button"
              onClick={handleClearFilter}
              className="rounded-full px-4 py-1.5 text-sm font-medium text-[#64748B] transition hover:bg-black/5"
            >
              {t('selfAssess.clearFilter')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="rounded-full px-5 py-1.5 text-sm font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: 'var(--sidebar-active)' }}
            >
              {t('component.confirm')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>,
    document.body,
  );
}
